import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import {
  Button,
  Dimmer,
  Loader,
  Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import qs from 'qs';
import PageNav from './PageNav';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';

import DeleteConfirm from '../containers/modals/DeleteConfirm';

class SpecificationPresetList extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    headerNode: PropTypes.func,
    footerNode: PropTypes.func,
    where: PropTypes.shape({
      limit: PropTypes.number,
    }),
    loading: PropTypes.bool,
    type: PropTypes.oneOf(['projects', 'unit_layouts', 'units']).isRequired,
  }
  constructor(props) {
    super(props);
    this.state = {
      count: 0,
      limit: 0,
      offset: 0,
      loading: props.loading || true,
      data: [],
      currentPage: 1,
      showConfirm: false,
      selectedPreset: null,
    };
  }
  componentDidMount() {
    this.fetchSpecifications(this.props.where);
  }
  setSpecifications(data = []) {
    return new Promise((res) => {
      this.setState({
        data: data || [],
      }, () => {
        res(data);
      });
    });
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  async fetchSpecification(id) {
    const { store } = this.context;
    const { t } = this.props;
    let res;
    let token;
    try {
      res = await store.dispatch(fetchM(`/specifications/${this.props.type}/${id}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return null;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      token = res.headers.get('X-Csrf-Token');
      // handle token.. etc
    }
    return token;
  }
  async fetchSpecifications(where = {}) {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    const query = Object.assign({}, where);
    query.page = this.state.currentPage || 1;
    let res;
    try {
      res = await store.dispatch(fetchM(`/specifications/${this.props.type}/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10) || 0;
      const data = await res.json();
      await this.setSpecifications(data);
      // handle token.. etc
      await this.setComponentState({
        count,
        limit,
        offset,
      });
    }
    await this.setLoading(false);
  }
  async deletePreset(id) {
    const { store } = this.context;
    const { t } = this.props;
    const token = await this.fetchSpecification(id);
    await this.setState({ loading: true });
    try {
      await store.dispatch(fetchM(`/specifications/${this.props.type}/${id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': token,
        },
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
    await this.setState({ loading: false });
    this.handleChangePage(1);
    store.dispatch(flashSuccessMessage({
      header: t('Delete Success', { ns: TRANSLATION_FILES.translation }),
      message: t('Preset successfully deleted', { ns: TRANSLATION_FILES.translation })
    }));
  }
  handleChangePage = (currentPage) => {
    this.setState({ currentPage }, () => {
      this.fetchSpecifications(this.props.where);
    });
  }
  handleCancelDelete = (e) => {
    e.preventDefault();
    this.setState({
      showConfirm: false,
      selectedPreset: null,
    });
  }
  handleDelete = (e, selectedPreset) => {
    e.preventDefault();
    this.setState({
      selectedPreset,
    }, () => {
      this.setState({
        showConfirm: true,
      });
    });
  }
  handleConfirmDelete = (e) => {
    e.preventDefault();
    this.setState({
      showConfirm: false,
    }, () => {
      this.deletePreset(this.state.selectedPreset.id);
    });
  }
  renderPath() {
    if (this.props.type === 'unit_layouts') {
      return 'layouts';
    }
    return this.props.type;
  }
  render() {
    const { count, limit, offset, loading, currentPage } = this.state;
    const { t } = this.props;
    return (
      <div>
        <DeleteConfirm
          open={this.state.showConfirm}
          onClose={this.handleCancelDelete}
          onConfirm={this.handleConfirmDelete}
          name={this.state.selectedPreset ? this.state.selectedPreset.name : ''}
        />
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.data.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Table
          striped unstackable
          selectable
        >
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4}>
                <Button
                  as={Link}
                  primary
                  size="small"
                  icon="add"
                  content={t('Preset')}
                  to={`/settings/${this.renderPath()}/new/`}
                />
                {
                  this.props.headerNode ?
                  this.props.headerNode({
                    count,
                    limit,
                    offset,
                    dataSize: this.state.data.length,
                    loading,
                    currentPage,
                    onChange: this.handleChangePage,
                  }) : (
                    <PageNav
                      floated="right"
                      currentPage={currentPage}
                      count={count}
                      limit={limit}
                      offset={offset}
                      dataSize={this.state.data.length}
                      loading={loading}
                      onChange={this.handleChangePage}
                    />
                  )
                }
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell width={4}>{t('Name')}</Table.HeaderCell>
              <Table.HeaderCell width={4}>{t('Short Name')}</Table.HeaderCell>
              <Table.HeaderCell colSpan={2}>{t('Description')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              this.state.data.length > 0 ?
              this.state.data.map((field, key) => (
                <Table.Row key={key}>
                  <Table.Cell width={4}>
                    <Link
                      to={`/settings/${this.renderPath()}/${field.id}/`}
                    >
                      {field.name}
                    </Link>
                  </Table.Cell>
                  <Table.Cell width={4}>
                    {field.shortName}
                  </Table.Cell>
                  <Table.Cell>
                    {field.description || '-'}
                  </Table.Cell>
                  <Table.Cell collapsing textAlign="right">
                    <Button
                      compact
                      negative
                      type="button"
                      size="tiny"
                      icon="cancel"
                      onClick={e => this.handleDelete(e, field)}
                    />
                  </Table.Cell>
                </Table.Row>
              )) : (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    {
                      this.state.loading ?
                        <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                      : <p>{t('No presets found')}.</p>
                    }
                  </Table.Cell>
                </Table.Row>
              )
            }
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="right">
                {
                  this.props.footerNode ?
                  this.props.footerNode({
                    count,
                    limit,
                    offset,
                    dataSize: this.state.data.length,
                    loading,
                    currentPage,
                    onChange: this.handleChangePage,
                  }) : (
                    <PageNav
                      upward
                      currentPage={currentPage}
                      count={count}
                      limit={limit}
                      offset={offset}
                      dataSize={this.state.data.length}
                      loading={loading}
                      onChange={this.handleChangePage}
                    />
                  )
                }
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    );
  }
}

export default withTranslation(namespaces)(SpecificationPresetList);



// WEBPACK FOOTER //
// ./src/components/SpecificationPresetList.jsx